import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import '@xyflow/react/dist/style.css';

import { MarketingTagsDropdown } from 'src/app/components/form-elements/dropdowns/marketing-tags-dropdown';
import { FormSection } from 'src/app/components/lib/form';
import { Input } from 'src/app/components/lib/input';
import { WizardCard } from 'src/app/components/lib/wizard/card';
import { WizardCardList } from 'src/app/components/lib/wizard/card/styled';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import { TimeUnitEnum } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/builder-context';
import { EVENT_TYPE_IDS } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/helpers';
import { SlideContainer } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/shared-slides/shared';
import { useGetLoyaltyDetails } from 'src/app/queries/loyalty/get-details';
import { useSaveWorkflowCampaignMutation } from 'src/app/queries/marketing/upsert-workflow-campaign';
import { Modal, useModal } from 'src/app/state/modals';
import { useNotificationActions } from 'src/app/state/notifications';

import { WORKFLOW_WIZARD_PATHS } from '../../../list';
import { useWorkflowBuilderContext } from '../../wizard/builder-context';
import { getSaveWorkflowCampaignPayload } from '../../wizard/helpers';

import type { WorkflowTemplate } from '../../wizard/builder-context';
import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';
import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

type Template = {
  description: string;
  name: string;
  template: WorkflowTemplate;
};

const templates: Template[] = [
  {
    name: 'Welcome',
    description: 'Welcome new customers who have signed up for loyalty',
    template: {
      DelayInMinutes: 0,
      DelayUnitId: TimeUnitEnum.hours,
      EventTypeId: EVENT_TYPE_IDS.LOYALTY_ENROLLMENT,
      SendToAllCustomers: true,
      TimeOfDayToSend: null,
      Nodes: [
        {
          Id: 1,
          Type: 'timeDelay',
          Duration: '01:00:00',
          TimeUnitId: TimeUnitEnum.hours,
          TimeOfDay: null,
        },
        {
          Id: 2,
          Type: 'action',
          ActionType: 'sendEmail',
          EmailConfig: {
            SubjectLine: '',
            PreviewTextLine: '',
            IsActive: true,
            MarketingEmailId: null,
            MarketingEmailTemplateId: null,
            MetaSenderEmail: null,
            ReplyToEmail: null,
            SenderEmail: null,
            MarketingTemplatePresetId: null,
            Priority: null,
            CustomUtmParameters: [],
          },
        },
      ],
      Edges: [
        {
          EdgeType: 'next',
          Source: 1,
          Target: 2,
        },
      ],
    },
  },
  {
    name: 'Birthday',
    description: "Celebrate your customer's birthday with a personal message and exclusive offer",
    template: {
      EventTypeId: EVENT_TYPE_IDS.UPCOMING_BIRTHDAY,
      DelayInMinutes: 4320, // 3 days
      DelayUnitId: TimeUnitEnum.days,
      TimeOfDayToSend: '15:00:00',
      SendToAllCustomers: true,
      Nodes: [
        {
          Id: 1,
          Type: 'action',
          ActionType: 'sendEmail',
          EmailConfig: {
            SubjectLine: '',
            PreviewTextLine: '',
            IsActive: true,
            MarketingEmailId: null,
            MarketingEmailTemplateId: null,
            MetaSenderEmail: null,
            ReplyToEmail: null,
            SenderEmail: null,
            MarketingTemplatePresetId: null,
            Priority: null,
            CustomUtmParameters: [],
          },
        },
      ],
      Edges: [],
    },
  },
];

export function WorkflowCampaignBasicInfo({
  enableNextButton,
  disableNextButton,
  onNextButtonClick,
  setActiveStepComplete,
}: WizardStepComponentProps) {
  const [builderState, dispatch] = useWorkflowBuilderContext();
  const { mutateAsync: saveWorkflowCampaign } = useSaveWorkflowCampaignMutation();
  const { data: loyaltyDetails } = useGetLoyaltyDetails();
  const notifications = useNotificationActions();
  const navigate = useNavigate();
  const confirmModal = useModal(Modal.confirm);
  const [hasOverriddenWithTemplate, setHasOverriddenWithTemplate] = useState(false);

  const useMessageLevelUtmFields = useLDFlag(LD.USE_MESSAGE_LEVEL_UTM_FIELDS, false);

  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const filteredTemplates = templates.filter((template) => {
    if (template.template.EventTypeId === EVENT_TYPE_IDS.LOYALTY_ENROLLMENT) {
      return loyaltyDetails?.LoyaltyRequireOptIn === true;
    }
    return true;
  });

  const setCampaignName = (name: string) => {
    dispatch({
      type: 'set-value',
      payload: {
        key: 'campaignName',
        value: name,
      },
    });
  };
  const setTags = (tags: number[]) => dispatch({ type: 'set-value', payload: { key: 'tagIds', value: tags } });
  const setUtmTerm = (utmTerm: string | null) => {
    dispatch({ type: 'set-value', payload: { key: 'utmTerm', value: utmTerm } });
  };

  useEffect(() => {
    if (builderState.campaignName) {
      enableNextButton();
    } else {
      disableNextButton();
    }
  }, [enableNextButton, disableNextButton, builderState.campaignName]);

  const handleSaveOnNextStep = useCallback(async () => {
    try {
      const response = await saveWorkflowCampaign(getSaveWorkflowCampaignPayload({}, builderState));
      navigate(`${WORKFLOW_WIZARD_PATHS.EDIT_WORKFLOW}/${String(response.MarketingCampaignId)}`, { replace: true });
      setActiveStepComplete();
    } catch (e) {
      const error = e as ApiErrorResponse;
      notifications.error({
        message: error?.response?.data?.Message ?? 'Error saving workflow',
      });
      throw e;
    }
  }, [builderState, saveWorkflowCampaign, navigate]);

  useEffect(() => {
    onNextButtonClick(handleSaveOnNextStep);
  }, [onNextButtonClick, handleSaveOnNextStep]);

  const onTemplateClick = useCallback(
    (template: Template) => {
      if (builderState.marketingCampaignId && !hasOverriddenWithTemplate) {
        confirmModal.openModal({
          title: 'Are you sure?',
          helpText: 'This will overwrite the current campaign with the template.',
          onConfirm: () => {
            setHasOverriddenWithTemplate(true);
            if (selectedTemplate === template) {
              dispatch({ type: 'clear-template' });
              setSelectedTemplate(null);
            } else {
              dispatch({ type: 'apply-template', payload: template.template });
              setSelectedTemplate(template);
            }
          },
        });
      } else if (selectedTemplate === template) {
        dispatch({ type: 'clear-template' });
        setSelectedTemplate(null);
      } else {
        dispatch({ type: 'apply-template', payload: template.template });
        setSelectedTemplate(template);
      }
    },
    [builderState.marketingCampaignId, selectedTemplate, confirmModal, dispatch, hasOverriddenWithTemplate]
  );

  type Template = {
    description: string;
    name: string;
    template: WorkflowTemplate;
  };

  return (
    <StyledSlideContainer>
      <EditorContainer>
        <StyledWizardCardList $maxWidth='786px'>
          <WizardCard
            description='Give your workflow a name that will allow you to easily understand its function. '
            title='Workflow name'
          >
            <FormSection>
              <Input
                data-testid='campaign-name-input'
                label='Campaign name:'
                labelPlacement='top'
                placeholder='Add a campaign name'
                required
                value={builderState.campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
              <MarketingTagsDropdown
                label='Tags:'
                labelPlacement='top'
                placeholder='Select tags'
                value={builderState.tagIds}
                onChange={(tags) => setTags(tags)}
              />
              {!useMessageLevelUtmFields && (
                <Input
                  data-testid='campaign-builder-utm-term-input'
                  label='UTM term:'
                  labelPlacement='top'
                  placeholder='Enter a UTM term'
                  tooltip='Links in any emails/SMS messages will include this as the utm_term parameter for analytics purposes'
                  value={builderState.utmTerm ?? ''}
                  onChange={(e) => setUtmTerm(e.target.value)}
                />
              )}
            </FormSection>
          </WizardCard>
          <WizardCard
            description='Select the workflow you would like to create based on these templates.'
            title='Template'
          >
            <TemplateContainer>
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.name}
                  selected={selectedTemplate === template}
                  onClick={() => onTemplateClick(template)}
                >
                  <TemplateTitle>{template.name}</TemplateTitle>
                  <TemplateDescription>{template.description}</TemplateDescription>
                </TemplateCard>
              ))}
            </TemplateContainer>
          </WizardCard>
        </StyledWizardCardList>
      </EditorContainer>
    </StyledSlideContainer>
  );
}

const TemplateDescription = styled.p`
  font: var(--font-extra-small-12pt-normal);
  color: var(--color-gray-80);
`;

const TemplateTitle = styled.label`
  font: var(--font-regular-14pt-semibold);
  color: var(--color-grayscale-black);
  margin: 0;
`;

const TemplateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sizes-20);
`;

const TemplateCard = styled.div<{ selected: boolean }>`
  cursor: pointer;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: var(--sizes-40);
  border-radius: 6px;
  gap: var(--sizes-20);
  border: 1px solid var(--color-gray-20);
  ${({ selected }) =>
    selected &&
    css`
      border-color: var(--color-brand-secondary-sapphire);
    `}
`;

const StyledSlideContainer = styled(SlideContainer)`
  overflow-y: hidden;
`;
const StyledWizardCardList = styled(WizardCardList)`
  margin: auto;
  padding-bottom: var(--sizes-50);
  padding-top: var(--sizes-50);
`;
const EditorContainer = styled.div`
  display: block;
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;
